import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { AggregationFilterTypeKeys, ConditionFiltersTypeKeys } from "api";
import { FiltersType, ISelectOption } from "components";
import {
  DiscordIcon,
  Entries,
  FacebookIcon,
  GitIcon,
  InvestorTypeEnum,
  LineIcon,
  LinkedinIcon,
  MAX_32_BIT_INT,
  NOT_AVAILABLE_INFO,
  Nullable,
  Optional,
  QUERY_STRINGS,
  SELECT_ALL_SELECT_OPTION,
  SkypeIcon,
  SlackIcon,
  SocialNetworks,
  StylingRelatedConstants,
  TelegramIcon,
  TwitterIcon,
  UNEXPECTED_UNICODES_REGEX,
  ViberIcon,
  WhatsappIcon,
} from "config";
import { InputRefType } from "hooks/types";
import { Theme } from "@mui/material";

export { DateHelper } from "./date.helper";

function parseBooleanString(value: unknown): unknown {
  if (value === "true") return true;

  if (value === "false") return false;

  return value;
}

export function tableHeightCalculationExpressions(theme: Theme) {
  return `100vh - ${theme.spacing(8)} - ${theme.spacing(5)} - ${theme.spacing(
    5
  )} - ${theme.spacing(5)} - ${theme.spacing(3)} - ${
    StylingRelatedConstants.TOPBAR_HEIGHT
  } - ${theme.spacing(6)} - ${StylingRelatedConstants.PAGINATION_HEIGHT}`;
}

export function transformFiltersForPayload(filters: FiltersType) {
  return Object.entries(filters).reduce(
    (filterAcc, [filterKey, filterValue]) => {
      const isArray = Array.isArray(filterValue.value);
      let payloadValue: any = parseBooleanString(filterValue.value);
      let filtersPayloadKeysType:
        | ConditionFiltersTypeKeys
        | AggregationFilterTypeKeys = isArray
        ? ConditionFiltersTypeKeys.IN
        : ConditionFiltersTypeKeys.EQ;
      if (filterValue?.isInput) {
        filtersPayloadKeysType = ConditionFiltersTypeKeys.CONTAINS;
      }
      if (filterValue?.isRange) {
        filtersPayloadKeysType = ConditionFiltersTypeKeys.BETWEEN;
        payloadValue = {
          from: +(filterValue.value as string).split(
            QUERY_STRINGS.SEPARATOR
          )[0],
          to:
            +(filterValue.value as string).split(QUERY_STRINGS.SEPARATOR)[1] ||
            MAX_32_BIT_INT,
        };
        if (filterValue?.isAggregation) {
          filtersPayloadKeysType = AggregationFilterTypeKeys.COUNT;
          payloadValue = {
            [ConditionFiltersTypeKeys.BETWEEN]: payloadValue,
          };
        }
      }

      return {
        ...filterAcc,
        [filterKey]: {
          [filtersPayloadKeysType]: payloadValue,
        },
      };
    },
    {}
  );
}

export function createBasicFilters(
  params: Record<string, string>,
  dynamicKeyForSearch = "name"
) {
  return Object.entries(params).reduce((acc, [key, value]) => {
    switch (key) {
      case QUERY_STRINGS.FILTERS: {
        const filtersValue: FiltersType = JSON.parse(value);
        return {
          ...acc,
          filters: {
            ...acc["filters"],
            ...transformFiltersForPayload(filtersValue),
          },
        };
      }
      case QUERY_STRINGS.SEARCH: {
        return {
          ...acc,
          filters: {
            ...acc["filters"],
            [dynamicKeyForSearch]: {
              [ConditionFiltersTypeKeys.CONTAINS]: value,
            },
          },
        };
      }
      case QUERY_STRINGS.LIMIT_PER_PAGE: {
        return {
          ...acc,
          paging: {
            ...acc.paging,
            limit: +value,
          },
        };
      }
      case QUERY_STRINGS.PAGINATION: {
        return {
          ...acc,
          paging: {
            ...acc.paging,
            page: +value - 1,
          },
        };
      }
      case QUERY_STRINGS.SORT: {
        return {
          ...acc,
          sorting: {
            orderBy: {
              field: value.split(QUERY_STRINGS.SEPARATOR)[0],
              direction: value.split(QUERY_STRINGS.SEPARATOR)[1].toUpperCase(),
            },
          },
        };
      }
      default: {
        return {
          ...acc,
        };
      }
    }
  }, {} as any);
}

export function capitalizeFirstLetter(str: string): string {
  const normalized = str.replace(/_/g, " ");
  const capitalized = normalized
    .toLowerCase()
    .replace(/^./, str[0].toUpperCase());

  return capitalized;
}

export function removeUnexpectedUnicodes(str: string): string {
  return str.replace(UNEXPECTED_UNICODES_REGEX, "");
}

export const SocialNetworkIconsMap: Record<SocialNetworks, () => JSX.Element> =
  {
    [SocialNetworks.DISCORD]: () => <DiscordIcon />,
    [SocialNetworks.LINE]: () => <LineIcon />,
    [SocialNetworks.TELEGRAM]: () => <TelegramIcon />,
    [SocialNetworks.TWITTER]: () => <TwitterIcon />,
    [SocialNetworks.WHATSAPP]: () => <WhatsappIcon />,
    [SocialNetworks.SLACK]: () => <SlackIcon />,
    [SocialNetworks.LINKEDIN]: () => <LinkedinIcon />,
    [SocialNetworks.SKYPE]: () => <SkypeIcon />,
    [SocialNetworks.VIBER]: () => <ViberIcon />,
    [SocialNetworks.FACEBOOK]: () => <FacebookIcon />,
    [SocialNetworks.GIT]: () => <GitIcon />,
  };

export function makeEnumToSelectiveOption<T extends string>(
  enums: {
    [e in T]: string;
  },
  capitalize = true,
  customOption: Record<string, string> = {}
) {
  return Object.values<string>(enums).map((el) => {
    if (customOption[el]) {
      return {
        value: el,
        label: customOption[el],
      };
    }
    return {
      value: el,
      label: capitalize ? capitalizeFirstLetter(el) : el,
    };
  });
}

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function generateColors(initialColor: string, count: number) {
  const colorArr = [];
  colorArr.push(initialColor);
  let magnitude = 20;

  if (count < 6) magnitude = 50;
  else if (count < 11) magnitude = 20;
  else if (count < 16) magnitude = 15;
  else if (count < 21) magnitude = 10;
  else magnitude = 5;

  while (count > 1) {
    initialColor = newShade(initialColor, magnitude);
    colorArr.push(initialColor);
    count--;
  }

  return colorArr;
}

function newShade(hexColor: string, magnitude: number) {
  hexColor = hexColor.replace(`#`, ``);

  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);

    return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
    return hexColor;
  }
}

export function camelize<T extends string>(str: T) {
  return str
    .replace(/\s(.)/g, (v) => v.toUpperCase())
    .replace(/\s/g, "")
    .replace(/^(.)/, (v) => v.toLowerCase());
}

export function separatePhoneNumberFromCode(phoneNumber: string): string {
  return phoneNumber
    .slice(phoneNumber.indexOf(")") + 1)
    .trim()
    .replace(/-|\s/g, "");
}

export const typedObjectKeys = <T extends Record<string, any>>(obj: T) =>
  Object.keys(obj) as (keyof T)[];

export function toggleHideDomElement(element: Element, delay = 1000) {
  element.classList.toggle("hide");
  setTimeout(() => {
    element.classList.toggle("hide");
  }, delay);
}

export function proceedNotAvailableData<T>(
  item: T,
  func?: (item: NonNullable<T>) => string
): string {
  if ([undefined, null].includes(item as any)) {
    return NOT_AVAILABLE_INFO;
  }

  return func ? func(item as NonNullable<T>) : (item as any).toString();
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = () => {};

export function transformFetchedDataIntoSelectiveOptions<
  T extends Record<string, any>
>(
  data: T[],
  keyOfId: keyof T,
  name: ((data: T) => string) | keyof T = "name",
  includeSelectAll?: boolean,
  selectAllLabel?: string,
  additionalPayloadFunc?: (data: T) => any
): ISelectOption[] {
  if (!Array.isArray(data) || !data.length) {
    return [];
  }

  const baseData: ISelectOption[] = includeSelectAll
    ? [
        {
          value: SELECT_ALL_SELECT_OPTION,
          label: selectAllLabel ?? "Select all",
        },
      ]
    : [];
  const selectsData = data.reduce((acc, el) => {
    acc.push({
      label: typeof name === "function" ? name(el) : el[name].toString(),
      value: el[keyOfId].toString(),
      additionalPayload: additionalPayloadFunc
        ? additionalPayloadFunc(el)
        : undefined,
    });
    return acc;
  }, baseData);

  return selectsData;
}

export const typedObjectEntries = <T extends Record<any, any>>(
  object: T
): Entries<T> => Object.entries(object) as Entries<T>;

export function removeLastBrackets(str: string) {
  const firstPart = str.slice(0, str.lastIndexOf("(") - 1);
  const result =
    firstPart + str.slice(str.lastIndexOf(")") + (Boolean(firstPart) ? 1 : 2));

  return result;
}

export const scrollErrorIntoView = (
  errors: Record<string, string>,
  refs: Optional<Nullable<InputRefType<string>>>
) => {
  if (refs) {
    const keys = Object.keys(errors);
    const foundKey = keys.find((el) => Boolean(refs?.[el]));

    if (foundKey) {
      refs[foundKey]?.scrollIntoView({
        behavior: "smooth",
      });
    }
  }
};

export const timezoneEntityToLabel = (name: string) => {
  const [standard, symbol, number] = name
    .replace(/MINUS/, "-")
    .replace(/PLUS/, "+")
    .split("_");

  return `${standard}${symbol && number ? `${symbol}${number}` : ""}`;
};

export const getSearchParamsObj = (searchParams: URLSearchParams) => {
  if (!searchParams || typeof searchParams[Symbol.iterator] !== "function") {
    return {};
  }

  const searchParamsObj: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    searchParamsObj[key] = value;
  });

  return searchParamsObj;
};

export async function exportDomElementAsPDF(
  element: HTMLDivElement,
  pdfTitle: string,
  hideTitle?: boolean
) {
  const rect = element.getBoundingClientRect();
  const width = rect.width / 2;
  const height = rect.height / 2;
  const pdfWidth = width + 100;
  const pdfHeight = height + 100;
  const x = 40;
  const y = 40;

  const canvas = await html2canvas(element);
  const pdf = new jsPDF(
    pdfHeight < pdfWidth ? "l" : "p",
    "px",
    [pdfHeight, pdfWidth],
    true
  );
  pdf.setFontSize(9);
  !hideTitle && pdf.text(pdfTitle, pdfWidth / 2 - pdfTitle.length * 1.5, 30);
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, y, width, height);
  pdf.save(`${pdfTitle}.pdf`);
}

export async function exportDomElementAsA4PDF(
  element: HTMLDivElement,
  documentName: string,
  title?: string | string[],
  forcePortrait?: boolean,
  margin = 20
) {
  const a4WidthPt = 595.28;
  const a4HeightPt = 841.89;
  const titleFontSize = 9;

  const canvas = await html2canvas(element);

  const elementWidth = canvas.width;
  const elementHeight = canvas.height;

  const isLandscapeBySize = elementWidth > elementHeight;
  const orientation =
    !forcePortrait && isLandscapeBySize ? "landscape" : "portrait";

  const pdf = new jsPDF({
    orientation,
    unit: "pt",
    format: "a4",
  });

  const pageWidth = orientation === "landscape" ? a4HeightPt : a4WidthPt;
  const pageHeight = orientation === "landscape" ? a4WidthPt : a4HeightPt;

  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin * 2;
  const titleLines = Array.isArray(title) ? title.length : 1;
  const titleSpacing = title ? titleFontSize * titleLines + 10 : 0;

  const scaleRatio = Math.min(usableWidth / elementWidth, 1);
  const imgWidth = elementWidth * scaleRatio;
  const imgHeight = elementHeight * scaleRatio;

  const totalPages = Math.ceil(imgHeight / (usableHeight - titleSpacing));

  for (let page = 0; page < totalPages; page++) {
    if (page > 0) pdf.addPage();

    if (title) {
      pdf.setFontSize(titleFontSize);
      const textWidth = Array.isArray(title)
        ? Math.max(...title.map((t) => pdf.getTextWidth(t)))
        : pdf.getTextWidth(title);
      const textX = (pageWidth - textWidth) / 2;
      pdf.text(
        Array.isArray(title) ? title.join("\n") : title,
        textX,
        margin + titleFontSize
      );
    }

    const srcY = ((usableHeight - titleSpacing) / scaleRatio) * page;
    const pageCanvas = document.createElement("canvas");

    pageCanvas.width = elementWidth;
    pageCanvas.height = Math.min(
      (usableHeight - titleSpacing) / scaleRatio,
      elementHeight - srcY
    );
    const pageCtx = pageCanvas.getContext("2d");

    pageCtx?.drawImage(canvas, 0, -srcY);

    const pageImgData = pageCanvas.toDataURL("image/png");
    const pageImgHeight = pageCanvas.height * scaleRatio;
    const imageY = margin + titleSpacing;

    pdf.addImage(pageImgData, "PNG", margin, imageY, imgWidth, pageImgHeight);
  }

  pdf.save(`${documentName}.pdf`);
}

export function mapInvestorName<T>(item: {
  id?: number;
  investorProfileId?: number;
  firstName?: Nullable<string>;
  lastName?: Nullable<string>;
  type: number;
  corporationName?: Nullable<string>;
  fileNumber?: T;
}): {
  id: number;
  fileNumber: T;
  fullName: string;
} {
  return {
    id: item.id || item.investorProfileId || 0,
    fileNumber: item.fileNumber as T,
    fullName:
      item.type === InvestorTypeEnum.INDIVIDUAL
        ? `${item.firstName} ${item.lastName}`
        : `${item.corporationName}`,
  };
}

export { calendarLocalizer } from "./calendar-localizer";
